import React, { useEffect, useState, useRef } from 'react';
import { PopoverMenu } from '../index';
import classNames from 'classnames';
import { Placement } from '@floating-ui/react';
export type DropdownOption = { label: string; value: string };

export interface DropdownProps {
  name: string;
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  onChange?: (event: { target: { name: string; value: string | null } }) => void;
  onBlur?: (event: { target: { name: string; value: string | null } }) => void;
  onClear?: () => void;
  displayEmpty?: boolean;
  className?: string;
  offset?: number;
  placement?: Placement;
  popoverMenuPortal?: boolean;
  contentWrapperClassName?: string;
  ref?: React.Ref<HTMLInputElement>; // React 19: ref as a prop
}

const Dropdown = ({
  name,
  options,
  value,
  placeholder,
  onChange,
  onBlur,
  onClear,
  displayEmpty,
  className,
  offset,
  placement,
  popoverMenuPortal,
  contentWrapperClassName,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [_value, set_value] = useState<string | null>(value ?? null);
  const [title, setTitle] = useState<string | null>(null);
  const wrapper = useRef<HTMLUListElement>(null);

  const _onChange = (option: DropdownOption) => {
    set_value(option.value);
    // Fabricated “event” with the bits consumers actually use
    const event = { target: { name, value: option.value } };
    onChange?.(event);

    setOpen(false);
    if (onBlur) {
      setTimeout(() => {
        onBlur(event);
      }, 0);
    }
  };

  useEffect(() => {
    if (_value != null) {
      const opt = options?.find((o) => o.value === _value);
      setTitle(opt?.label ?? null);
    } else {
      setTitle(null);
    }
  }, [_value, options]);

  useEffect(() => {
    if (value !== null && value !== undefined) {
      set_value(value);
    } else {
      set_value(null);
      setTitle(null);
    }
  }, [value]);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      const focusOption = wrapper.current?.querySelector('.option:focus');
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          const prev = focusOption?.previousElementSibling as HTMLElement | null | undefined;
          prev?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          const next = focusOption?.nextElementSibling as HTMLElement | null | undefined;
          next?.focus();
          break;
        case 'Enter':
          event.preventDefault();
          (focusOption as HTMLElement | null | undefined)?.click();
          break;
      }
    };

    if (open) {
      window.addEventListener('keydown', keydown);
    } else {
      window.removeEventListener('keydown', keydown);
    }

    return () => window.removeEventListener('keydown', keydown);
  }, [open]);

  const handleClear = () => {
    set_value(null);
    setTitle(null);
    onClear?.();
    const event = { target: { name, value: null } };
    onChange?.(event);
  };

  return (
    <PopoverMenu
      open={open}
      title={title || placeholder || ''}
      titleClass={!title && placeholder ? 'placeholder' : ''}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      className={classNames(title && 'selected', '[&_.main-button]:h-10', className)}
      offset={offset}
      placement={placement}
      portal={popoverMenuPortal}
      contentWrapperClassName={contentWrapperClassName}
      showClearIcon={onClear && !!title}
      onClearIconClick={handleClear}
    >
      <ul className="w-full py-2 text-sm text-gray-700 dark:text-gray-200" ref={wrapper}>
        {displayEmpty && (
          <li
            onClick={() => _onChange({ value: '', label: '' })}
            className="option hover:bg-gray-100"
            tabIndex={open ? 0 : -1}
          >
            <div className="cursor-pointe block px-4 py-2">&nbsp;</div>
          </li>
        )}
        {options &&
          options.map((option) => (
            <li
              key={option.value}
              onClick={() => _onChange(option)}
              className={classNames(
                option.value === _value && 'selected bg-gray-300 hover:!bg-gray-300',
                'option outline-none hover:bg-gray-100 focus:bg-gray-100',
              )}
              tabIndex={open ? 0 : -1}
            >
              <div
                className={classNames('block cursor-pointer px-4 py-2')}
                dangerouslySetInnerHTML={{ __html: option.label?.replace(/<(p)>/g, '').replace(/<\/(p)>/g, '') }}
              />
            </li>
          ))}
      </ul>
    </PopoverMenu>
  );
};

export default Dropdown;
